import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addNotification } from '../../store/slices/uiSlice';
import {
  enableLiveTracking,
  loadLiveTracking,
  removeLiveTracking,
} from '../../store/slices/liveTrackingSlice';

const EnableLiveTrackingButton = ({
  itemType,
  taskId,
  sourceMessageId,
  sourceChannelId,
  title,
  assigneeUserId,
  assigneeName,
  boardLane,
  compact = false,
}) => {
  const dispatch = useDispatch();
  const items = useSelector((state) => state.liveTracking?.items || []);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    dispatch(loadLiveTracking());
  }, [dispatch]);

  const tracked = useMemo(() => {
    if (itemType === 'TASK' && taskId) {
      return items.find((item) => item.itemType === 'TASK' && String(item.taskId || item.sourceKey) === String(taskId));
    }
    if (itemType === 'WORKFLOW' && sourceMessageId) {
      return items.find((item) => (
        item.itemType === 'WORKFLOW'
        && String(item.sourceMessageId || item.sourceKey) === String(sourceMessageId)
      ));
    }
    return null;
  }, [items, itemType, taskId, sourceMessageId]);

  const canToggle = itemType === 'TASK' ? Boolean(taskId) : Boolean(sourceMessageId);
  if (!canToggle) return null;

  const onToggle = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (busy) return;
    setBusy(true);
    try {
      if (tracked?.id) {
        await dispatch(removeLiveTracking(tracked.id)).unwrap();
        dispatch(addNotification({ type: 'success', message: 'Removed from Live Tracking' }));
      } else {
        await dispatch(enableLiveTracking({
          itemType,
          taskId: taskId || undefined,
          sourceMessageId: sourceMessageId || undefined,
          sourceChannelId: sourceChannelId || undefined,
          title: title || undefined,
          assigneeUserId: assigneeUserId || undefined,
          assigneeName: assigneeName || undefined,
          boardLane: boardLane || 'TEAM',
        })).unwrap();
        dispatch(addNotification({ type: 'success', message: 'Added to Live Tracking' }));
      }
    } catch (error) {
      dispatch(addNotification({
        type: 'error',
        message: typeof error === 'string' ? error : 'Could not update live tracking',
      }));
    } finally {
      setBusy(false);
    }
  };

  const active = Boolean(tracked);
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={busy}
      title={active ? 'Disable live tracking' : 'Enable live tracking'}
      className={`${compact ? 'px-2 py-1 text-[10px]' : 'px-2.5 py-1 text-[11px]'} inline-flex items-center gap-1 rounded-full font-semibold border transition-colors disabled:opacity-60 ${
        active
          ? 'bg-gradient-to-r from-fuchsia-500 to-cyan-400 text-white border-transparent shadow-sm'
          : 'border-fuchsia-300 text-fuchsia-700 bg-fuchsia-50 hover:bg-fuchsia-100 dark:border-fuchsia-700 dark:text-fuchsia-200 dark:bg-fuchsia-950/40'
      }`}
    >
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${active ? 'bg-white animate-pulse' : 'bg-fuchsia-500'}`} />
      {active ? 'Live tracking on' : 'Enable live tracking'}
    </button>
  );
};

export default EnableLiveTrackingButton;
